import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { Authenticated, AuthLoading, Unauthenticated, useAction, useQuery } from "convex/react";
import { useAuth } from "@workos-inc/authkit-react";
import { api } from "../../convex/_generated/api";
import { hasConvexConfig, hasWorkOsConfig } from "../lib/config";
import { WorkOsUserControls } from "./WorkOsUserControls";
import { clearInviteToken, readInviteToken } from "../lib/inviteToken";
import clsx from "clsx";
import { useEffect, useState } from "react";

function MeChip() {
  const me = useQuery(api.organizations.me);
  if (!me?.user) return null;
  return (
    <>
      <span className="role-pill">{me.role}</span>
      <span className="user-name">{me.user.name}</span>
      {me.organization && (
        <span className="org-chip" title={me.organization.slug}>
          {me.organization.name}
        </span>
      )}
    </>
  );
}

/** AuthKit client User fields — display only; Convex never trusts these for authz. */
function useUnverifiedAuthKitProfile() {
  const { user } = useAuth();
  if (!user) return undefined;
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return {
    email: user.email || undefined,
    name: name || undefined,
    picture: user.profilePictureUrl ?? undefined,
  };
}

function PrimaryNav({ isAdmin }: { isAdmin: boolean }) {
  return (
    <nav className="nav" aria-label="Primary">
      <NavLink to="/" end className={({ isActive }) => clsx({ active: isActive })}>
        Dashboard
      </NavLink>
      <NavLink to="/inventory" className={({ isActive }) => clsx({ active: isActive })}>
        Inventory
      </NavLink>
      <NavLink to="/orders" className={({ isActive }) => clsx({ active: isActive })}>
        Orders
      </NavLink>
      <NavLink to="/locations" className={({ isActive }) => clsx({ active: isActive })}>
        Locations
      </NavLink>
      <NavLink to="/suppliers" className={({ isActive }) => clsx({ active: isActive })}>
        Suppliers
      </NavLink>
      {isAdmin ? (
        <NavLink to="/admin" className={({ isActive }) => clsx({ active: isActive })}>
          Admin
        </NavLink>
      ) : null}
    </nav>
  );
}

/**
 * Renders top nav only after syncIdentity confirms active membership.
 * Pending/denied users get a bare page with no primary nav.
 */
function AccessAwareShell() {
  const syncIdentity = useAction(api.workosActions.syncIdentity);
  const profile = useUnverifiedAuthKitProfile();
  const [attempt, setAttempt] = useState(0);
  const [gate, setGate] = useState<"syncing" | "ready" | "pending" | "denied" | "error">("syncing");
  const [role, setRole] = useState<string | null>(null);
  const [accessMessage, setAccessMessage] = useState<string | null>(null);
  const [accessEmail, setAccessEmail] = useState<string | null>(null);
  const [denialReason, setDenialReason] = useState<string | null>(null);
  const profileEmail = profile?.email;
  const profileName = profile?.name;
  const profilePicture = profile?.picture;
  const location = useLocation();

  useEffect(() => {
    let active = true;
    void syncIdentity({
      profile:
        profileEmail || profileName || profilePicture
          ? { email: profileEmail, name: profileName, picture: profilePicture }
          : undefined,
      inviteToken: readInviteToken() ?? undefined,
    })
      .then((result) => {
        if (!active) return;
        setAccessMessage(result?.message ?? null);
        setAccessEmail(result?.email ?? profileEmail ?? null);
        setDenialReason(result?.denialReason ?? null);
        if (result?.hasAccess) {
          clearInviteToken();
          setRole(result.role ?? "viewer");
          setGate("ready");
        } else if (result?.membershipStatus === "pending") {
          setGate("pending");
        } else {
          setGate("denied");
        }
      })
      .catch(() => {
        if (active) setGate("error");
      });
    return () => {
      active = false;
    };
  }, [attempt, syncIdentity, profileEmail, profileName, profilePicture]);

  if (gate === "syncing") {
    return (
      <>
        <header className="topbar">
          <div className="brand-block">
            <span className="brand">Track</span>
          </div>
        </header>
        <main className="main">
          <p className="muted">Preparing your workspace…</p>
        </main>
      </>
    );
  }

  if (gate === "pending" || gate === "denied") {
    const signedInEmail = accessEmail ?? profileEmail;
    const showAttemptNote =
      gate === "denied" &&
      (denialReason === "off_domain" ||
        denialReason === "rejected" ||
        denialReason === "no_verified_email");
    const pendingCopy =
      accessMessage ??
      "Your sign-in was recorded. An admin must approve your access before you can use Track.";
    const deniedCopy =
      accessMessage ??
      "Ask an admin to invite you or approve your email domain for this workspace.";

    return (
      <>
        <header className="topbar bare-topbar">
          <div className="brand-block">
            <span className="brand">Track</span>
          </div>
          <div className="user-area">
            <WorkOsUserControls />
          </div>
        </header>
        <main className="main">
          <section className="setup-gate pending-gate">
            <h1>{gate === "pending" ? "Awaiting approval" : "No workspace access"}</h1>
            <p>{gate === "pending" ? pendingCopy : deniedCopy}</p>
            {signedInEmail ? <p className="muted">Signed in as {signedInEmail}</p> : null}
            {gate === "pending" ? (
              <p className="muted">An admin will see your request under Pending approvals.</p>
            ) : null}
            {showAttemptNote ? (
              <p className="muted">
                An admin can see this sign-in under Access attempts and invite you from there.
              </p>
            ) : null}
            {denialReason === "no_verified_email" ? (
              <div className="banner conflict" role="status">
                <p>
                  Your WorkOS access token has no <code>email</code> claim. An admin must set the
                  WorkOS JWT Template (Admin → WorkOS JWT setup), then you must sign out and sign in
                  again.
                </p>
              </div>
            ) : null}
          </section>
        </main>
      </>
    );
  }

  if (gate === "error") {
    return (
      <>
        <header className="topbar bare-topbar">
          <div className="brand-block">
            <span className="brand">Track</span>
          </div>
        </header>
        <main className="main">
          <section className="setup-gate">
            <h1>Could not prepare your workspace</h1>
            <p>Check your connection and try again.</p>
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                setGate("syncing");
                setAttempt((value) => value + 1);
              }}
            >
              Retry
            </button>
          </section>
        </main>
      </>
    );
  }

  if (location.pathname.startsWith("/admin") && role !== "admin") {
    return (
      <>
        <header className="topbar">
          <div className="brand-block">
            <NavLink to="/" className="brand">
              Track
            </NavLink>
          </div>
          <PrimaryNav isAdmin={false} />
          <div className="user-area">
            <MeChip />
            <WorkOsUserControls />
          </div>
        </header>
        <main className="main">
          <Navigate to="/" replace />
        </main>
      </>
    );
  }

  return (
    <>
      <header className="topbar">
        <div className="brand-block">
          <NavLink to="/" className="brand">
            Track
          </NavLink>
        </div>
        <PrimaryNav isAdmin={role === "admin"} />
        <div className="user-area">
          <MeChip />
          <WorkOsUserControls />
        </div>
      </header>
      <main className="main">
        <Outlet />
      </main>
    </>
  );
}

export function AppShell() {
  const authenticationConfigured = hasConvexConfig && hasWorkOsConfig;

  return (
    <div className="app-shell">
      {!authenticationConfigured ? (
        <>
          <header className="topbar">
            <div className="brand-block">
              <NavLink to="/" className="brand">
                Track
              </NavLink>
            </div>
            <div className="user-area">{hasWorkOsConfig ? <WorkOsUserControls /> : null}</div>
          </header>
          <main className="main">
            <Outlet />
          </main>
        </>
      ) : (
        <>
          <AuthLoading>
            <header className="topbar">
              <div className="brand-block">
                <span className="brand">Track</span>
              </div>
            </header>
            <main className="main">
              <p className="muted">Checking your session…</p>
            </main>
          </AuthLoading>
          <Unauthenticated>
            <header className="topbar">
              <div className="brand-block">
                <span className="brand">Track</span>
              </div>
              <div className="user-area">
                <WorkOsUserControls />
              </div>
            </header>
            <main className="main">
              <section className="setup-gate">
                <h1>Sign in to Track</h1>
                <p>Only invited users can access orders and inventory.</p>
                <WorkOsUserControls />
              </section>
            </main>
          </Unauthenticated>
          <Authenticated>
            <AccessAwareShell />
          </Authenticated>
        </>
      )}
    </div>
  );
}
